import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Fingerprint, Pencil, X, Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import ExtractionConfirmPanel from "./ExtractionConfirmPanel";

const Chip = ({ children, tone = "slate", testid }) => {
  const tones = {
    slate: "border-slate-700 bg-slate-800/60 text-slate-300",
    cyan: "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
    blue: "border-blue-500/40 bg-blue-500/10 text-blue-300",
    emerald: "border-emerald-500/50 bg-emerald-500/10 text-emerald-300",
  };
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded border font-mono-data ${tones[tone]}`} data-testid={testid}>
      {children}
    </span>
  );
};

/**
 * Read-only summary of the quote's Single Source of Truth, with an editor
 * that PATCHes it and triggers the deterministic downstream recalculation
 * (operations → times → machine → costs → guardrail).
 */
export default function FeatureExtractionCard({ quote, onQuoteUpdate }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fx = quote?.feature_extraction;
  if (!fx) return null;

  const form = fx.raw_material_form || {};
  const ext = fx.external_diameters || [];
  const int = fx.internal_diameters || [];
  const threads = fx.threads || [];
  const procs = (fx.processes || []).map((p) => p.type);

  const openEditor = () => {
    setDraft(JSON.parse(JSON.stringify(fx)));
    setError("");
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post(`/quotes/${quote.id}/update-analysis`, {
        feature_extraction: draft,
      });
      onQuoteUpdate?.(data);
      setOpen(false);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Errore");
    }
    setSaving(false);
  };

  return (
    <div className="tech-card mb-6 p-4" data-testid="feature-extraction-card">
      <div className="flex items-center gap-2 mb-3">
        <Fingerprint size={14} className="text-cyan-400" />
        <div className="label-tech text-cyan-300">{t("extractionConfirm.ssotTitle")}</div>
        {fx.confirmed && <Chip tone="emerald" testid="fx-confirmed-chip">{t("extractionConfirm.conf.user_confirmed")}</Chip>}
        <button
          type="button"
          onClick={openEditor}
          className="ml-auto text-[11px] px-2 py-1 rounded border border-slate-700 text-slate-300 hover:border-amber-500 hover:text-amber-300 inline-flex items-center gap-1.5"
          data-testid="edit-features-btn"
        >
          <Pencil size={11} /> {t("extractionConfirm.editFeatures")}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-[10px] label-tech text-slate-500 mb-1">{t("extractionConfirm.rawForm")}</div>
          <Chip tone="blue" testid="fx-raw-form">{t(`extractionConfirm.forms.${form.value || "unknown"}`)}</Chip>
        </div>
        <div>
          <div className="text-[10px] label-tech text-slate-500 mb-1">{t("extractionConfirm.processes")}</div>
          <div className="flex flex-wrap gap-1" data-testid="fx-processes">
            {procs.length === 0 ? <span className="text-xs text-slate-500">—</span> :
              procs.map((p) => <Chip key={p}>{t(`extractionConfirm.proc.${p}`)}</Chip>)}
          </div>
        </div>
        <div>
          <div className="text-[10px] label-tech text-slate-500 mb-1">{t("extractionConfirm.externalDiameters")}</div>
          <div className="flex flex-wrap gap-1" data-testid="fx-external">
            {ext.length === 0 ? <span className="text-xs text-slate-500">—</span> :
              ext.map((d, i) => <Chip key={`e${i}`} tone="cyan">Ø{d.value_mm}{d.tolerance ? ` ${d.tolerance}` : ""}</Chip>)}
          </div>
        </div>
        <div>
          <div className="text-[10px] label-tech text-slate-500 mb-1">{t("extractionConfirm.internalDiameters")}</div>
          <div className="flex flex-wrap gap-1" data-testid="fx-internal">
            {int.length === 0 ? <span className="text-xs text-slate-500">—</span> :
              int.map((d, i) => <Chip key={`i${i}`} tone="cyan">Ø{d.value_mm}{d.tolerance ? ` ${d.tolerance}` : ""}</Chip>)}
          </div>
        </div>
      </div>

      {threads.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-800">
          <div className="text-[10px] label-tech text-slate-500 mb-1">{t("extractionConfirm.threads")}</div>
          <div className="flex flex-wrap gap-1" data-testid="fx-threads">
            {threads.map((th, i) => (
              <Chip key={`t${i}`} tone="blue">{th.designation} ×{th.quantity}</Chip>
            ))}
          </div>
        </div>
      )}

      {(fx.consistency_warnings || []).length > 0 && (
        <ul className="mt-3 pt-3 border-t border-slate-800 space-y-1 text-xs text-amber-200" data-testid="fx-warnings">
          {fx.consistency_warnings.map((w) => (
            <li key={w} className="flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              {t(`extractionConfirm.warn.${w}`, { defaultValue: w.replace(/_/g, " ") })}
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto p-4 sm:p-8" data-testid="feature-editor-modal">
          <div className="max-w-3xl mx-auto bg-slate-900 border border-slate-700 rounded-lg p-6 relative">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 p-1.5 rounded text-slate-400 hover:text-slate-100 hover:bg-slate-800"
              data-testid="close-feature-editor"
            >
              <X size={16} />
            </button>
            <ExtractionConfirmPanel
              extraction={draft}
              onChange={setDraft}
              confirmed
              onConfirm={() => {}}
            />
            {error && (
              <div className="mt-4 px-3 py-2 rounded bg-red-500/10 border border-red-500/40 text-red-300 text-sm" data-testid="feature-editor-error">
                {error}
              </div>
            )}
            <p className="mt-4 text-xs text-slate-400">{t("extractionConfirm.cascadeHint")}</p>
            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 rounded-md text-sm text-slate-300 border border-slate-700 hover:border-slate-500"
                data-testid="cancel-feature-editor"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="save-features-btn"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                {t("extractionConfirm.saveFeatures")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
